<script>
  import { page } from "$app/stores";
  import Button from "$lib/components/Button.svelte";
  import PrimaryMenu from "$lib/components/MenuTypes/PrimaryMenu.svelte";
  import UtilityMenu from "$lib/components/MenuTypes/UtilityMenu.svelte";
  import NavToggleIcon from "$lib/components/NavToggleIcon.svelte";
  import { shouldHideButton } from "$lib/components/MenuTypes/HeaderMenuRow.svelte";
  import { onMount } from "svelte";
  import FundraiseUpElement from "$lib/contentTypes/FundraiseUpElement.svelte";
  import LinkComponent from "$lib/contentTypes/LinkComponent.svelte";

  export let utilityMenu;
  export let primaryMenu;
  export let productRootUrl;

  let clazz = "";
  export { clazz as class };

  // Data has multiple header rows if all entries in the array have a children parameter
  const hasMultipleHeaderRows = [utilityMenu, primaryMenu].every(
    (menuItem) => menuItem.children !== undefined
  );

  const logoLink = primaryMenu.children.find((menuItem) => menuItem.type === "logoLink");

  const menuActionButton = primaryMenu.children.find(
    (menuItem) =>
      menuItem.type && menuItem.type !== "logoLink" && menuItem.type !== "productMenuLink"
  );

  if (menuActionButton) {
    menuActionButton.size = "headerActionButton";
  }

  const fuButton = primaryMenu.children.find(
    (menuItem) => menuItem.contentType === "fundraiseUpElement"
  );

  let menuRef;
  export let isOpen = false; // expose for testing
  let triggerHeight;
  let headerHeight;
  $: menuText = isOpen ? "Close" : "Menu";

  let verticalScrollPre = 0;
  let verticalScrollNext = 0;
  let verticalScrollLockPosition = 0;
  let scrollingUp = false;

  onMount(async () => {
    if (headerHeight) {
      document.documentElement.style.setProperty("--scroll-header-padding", `${headerHeight}px`);
    }
  });

  $: {
    scrollingUp = verticalScrollPre > verticalScrollNext;
    verticalScrollPre = verticalScrollNext;
  }

  $: shouldTranslate =
    !scrollingUp && verticalScrollNext > triggerHeight && verticalScrollLockPosition === 0;

  function toggleMobileMenu(expanded = false) {
    isOpen = expanded;
  }

  function toggleBodyScroll(shouldFreeze = false) {
    const body = document.body;
    if (shouldFreeze) {
      // when menu is expanded, freeze page body
      verticalScrollLockPosition = verticalScrollNext;
      body.style.position = "fixed";
      body.style.top = `-${verticalScrollLockPosition}px`;
    } else {
      // when menu is collapsed, reset and scroll to prev position
      body.style.position = "";
      body.style.top = "";
      window.scrollTo({
        top: verticalScrollLockPosition
      });
      verticalScrollLockPosition = 0;
    }
  }

  function calcMenuHeight(node) {
    const winHeight = window?.visualViewport?.height || window?.innerHeight;
    const fundraiseUpBarHeight = 40;
    const fundraiseUpBar = import.meta.env.VITE_FUNDRAISE_UP_LIVE_MODE !== "true";
    if (fundraiseUpBar) {
      node.style = `height: calc(${winHeight}px - ${triggerHeight}px - ${fundraiseUpBarHeight}px)`;
    } else {
      node.style = `height: calc(${winHeight}px - ${triggerHeight}px)`;
    }
  }

  function handleMenuToggle(e) {
    e.preventDefault();

    isOpen = !isOpen;
    toggleMobileMenu(isOpen);
    toggleBodyScroll(isOpen);
  }

  function handleJumpLinksInHeaderMenu(e) {
    if (e.detail.url.includes("#")) {
      isOpen = false;
      toggleBodyScroll();
    }
  }

  function handleResize() {
    const onDesktop = menuRef?.clientWidth >= 1366;
    if (onDesktop) {
      toggleMobileMenu(false);
      toggleBodyScroll(false);
    }
    calcMenuHeight(menuRef);
  }

  function doMenuHeightCalc(node) {
    calcMenuHeight(node);
    return {
      update(node) {
        calcMenuHeight(node);
      }
    };
  }
</script>

<header
  class="sticky top-0 z-10 w-screen transition-transform duration-1000 lg:translate-y-0"
  class:translate-y-[-100%]={shouldTranslate}
  bind:clientHeight={headerHeight}
>
  <!-- mobile action button and menu open/close button -->
  <div
    class="lg:hidden flex flex-col justify-between items-center bg-white border-b-2 border-neutral-300 h-[88px]"
    bind:clientHeight={triggerHeight}
  >
    <div class="flex flex-row justify-between items-center w-full h-full p-24">
      <!-- logo -->
      <LinkComponent data={logoLink} />

      <div class="flex justify-end items-center gap-16">
        <!-- donate or sign up button -->
        {#if menuActionButton && !shouldHideButton($page.url?.pathname, menuActionButton)}
          <LinkComponent data={menuActionButton} />
        {/if}

        <!-- fu button -->
        {#if fuButton}
          <FundraiseUpElement data={fuButton} />
        {/if}

        <!-- menu toggle-->
        <Button
          class="flex flex-col !ring-none !bg-none !shadow-none p-0"
          variant="ghost"
          size="base"
          on:click={handleMenuToggle}
          aria-expanded={isOpen}
        >
          <NavToggleIcon {isOpen} />
          <span>{menuText}</span>
        </Button>
      </div>
    </div>
  </div>

  <!-- Menu contents -->
  <div
    class="absolute w-full lg:static flex lg:flex flex-col overflow-y-scroll overscroll-y-contain lg:overflow-y-visible bg-neutral-100 lg:!h-auto {clazz}"
    class:hidden={!isOpen}
    bind:this={menuRef}
    use:doMenuHeightCalc
  >
    {#if hasMultipleHeaderRows}
      <UtilityMenu data={utilityMenu.children} {productRootUrl} />
    {/if}
    <PrimaryMenu on:urlDispatch={handleJumpLinksInHeaderMenu} data={primaryMenu.children} />
  </div>
</header>

<svelte:window bind:scrollY={verticalScrollNext} on:resize={handleResize} />
